/**
 * PostHog Analytics boot file.
 *
 * Responsibilities:
 *   - Skip entirely in SSR (process.env.SERVER).
 *   - In DEV mode: skip posthog.init() but export working stubs that log to console.debug.
 *   - Require VITE_POSTHOG_API_KEY in prod; log console.error and become a no-op if absent.
 *   - Initialise PostHog with privacy-safe defaults (opt-out by default, no autocapture,
 *     no session recording, no page-view capture — all controlled manually).
 *   - Gate capturing behind analytics consent (korum.consent.v2).
 *   - Listen to korum:consent-change for runtime opt-in / opt-out.
 *
 * API:
 *   captureEvent(name, props?)  — Track a named event.
 *   identifyMember(userId, props?) — Identify the current user in PostHog.
 *   groupWorkspace(workspaceId, props?) — Associate events with a workspace group.
 *   resetIdentity() — Detach the current user identity (call before sign-out).
 *
 * Design decisions:
 *   - D2: In DEV, captureEvent / identifyMember / groupWorkspace / resetIdentity
 *     print to console.debug instead of calling posthog-js.
 *   - D3: No callsite imports posthog-js directly — all traffic goes through this module.
 *   - H10: resetIdentity() must be called BEFORE setSession(null) to flush events.
 */

import { boot } from 'quasar/wrappers';
import { useConsent } from '@/composables/useConsent';

let initialized = false;

// Lazily-loaded posthog instance — only assigned in prod after init.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let ph: any = null;

/**
 * Track a custom PostHog event.
 * SSR-safe and DEV-safe — prints to console.debug in non-prod environments.
 *
 * @param name  - Event name (e.g. 'debate_launched').
 * @param props - Optional key/value properties.
 */
export const captureEvent = (name: string, props?: Record<string, unknown>): void => {
  if (typeof window === 'undefined') return;
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.debug('[posthog]', name, props);
    return;
  }
  if (!ph) return;
  ph.capture(name, props);
};

/**
 * Identify the authenticated member in PostHog.
 * SSR-safe and DEV-safe.
 *
 * @param userId - Supabase user UUID.
 * @param props  - Optional user properties (email, plan, …).
 */
export const identifyMember = (userId: string, props?: Record<string, unknown>): void => {
  if (typeof window === 'undefined') return;
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.debug('[posthog] identify', userId, props);
    return;
  }
  if (!ph) return;
  ph.identify(userId, props);
};

/**
 * Associate the current session with a workspace group in PostHog.
 * SSR-safe and DEV-safe.
 *
 * @param workspaceId - Workspace UUID.
 * @param props       - Optional group properties (plan, …).
 */
export const groupWorkspace = (workspaceId: string, props?: Record<string, unknown>): void => {
  if (typeof window === 'undefined') return;
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.debug('[posthog] group workspace', workspaceId, props);
    return;
  }
  if (!ph) return;
  ph.group('workspace', workspaceId, props);
};

/**
 * Reset the PostHog identity (anonymous session). Call BEFORE sign-out so
 * any queued events are flushed under the previous identity.
 * SSR-safe and DEV-safe.
 */
export const resetIdentity = (): void => {
  if (typeof window === 'undefined') return;
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.debug('[posthog] reset');
    return;
  }
  if (!ph) return;
  ph.reset();
};

export default boot(async () => {
  // Skip SSR — window is not available.
  if (process.env.SERVER) return;

  // Skip in development — no PostHog traffic in dev (D2).
  if (import.meta.env.DEV) return;

  // Idempotence guard for HMR.
  if (initialized) return;

  const apiKey = import.meta.env.VITE_POSTHOG_API_KEY as string | undefined;
  if (!apiKey) {
    // D4 — graceful no-op if key is missing.
    // eslint-disable-next-line no-console
    console.error('[posthog] Missing VITE_POSTHOG_API_KEY — instrumentation disabled.');
    return;
  }

  const apiHost = (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ?? 'https://eu.i.posthog.com';

  // Dynamic import keeps posthog-js out of the initial chunk (SSR-safe).
  const posthog = (await import('posthog-js')).default;

  posthog.init(apiKey, {
    api_host: apiHost,
    // Users must actively opt-in to analytics consent before any event is sent.
    opt_out_capturing_by_default: true,
    // Manual page-view tracking via router guard (no autocapture).
    autocapture: false,
    capture_pageview: false,
    // Session recording disabled per privacy policy.
    disable_session_recording: true,
    persistence: 'localStorage+cookie'
  });

  ph = posthog;
  initialized = true;

  const { hasConsent } = useConsent();

  // Initial check — opt in if the user already consented (returning visitor).
  if (hasConsent('analytics')) {
    posthog.opt_in_capturing();
  }

  // React to runtime consent changes (banner toggle, manage cookies modal).
  window.addEventListener('korum:consent-change', () => {
    if (hasConsent('analytics')) {
      posthog.opt_in_capturing();
    } else {
      posthog.opt_out_capturing();
    }
  });
});
